import React from "react";

import { Drawer, DrawerProps } from "@mui/material";

import {
  Container,
  Main,
  MainTitle,
  MainTitleLabel,
  MainTitleCloseButton,
  MainContent,
  Section,
  SectionTitle,
  SectionContent,
  Footer,
} from "./styled";

type DomNode = string | React.ReactNode | number | null;

interface SectionItem {
  id: string | number;
  title?: DomNode;
  content?: React.ReactNode;
  contentDisableGutters?: boolean;
  contentDisableMinHeight?: boolean;
}

export interface AppDrawerProps {
  title?: DomNode;
  footer?: DomNode;
  fullHeight?: boolean;
  DrawerProps?: DrawerProps;
  children?: React.ReactNode;
  idPrefix?: string;
  hideSideMenu?: boolean;
  disableGap?: boolean;
  disableModalPortal?: boolean;
  maxWidth?: string;

  sectionItems?: SectionItem[];
}

export const AppDrawer = (props: AppDrawerProps) => {
  const {
    title,
    footer,
    fullHeight = true,
    DrawerProps,
    sectionItems,
    disableGap,
    children,
    disableModalPortal = true,
    idPrefix = "app-drawer-",
    maxWidth,
  } = props;
  const { onClose } = DrawerProps ?? {};
  const idGenerator = (id: string | number) => {
    return `${idPrefix}${id}`;
  };

  const renderSections = () => {
    if (!sectionItems) return;

    return sectionItems.map((item: SectionItem) => {
      const { contentDisableGutters = true, contentDisableMinHeight = true } = item;

      return (
        <Section key={item.id} id={idGenerator(item.id)}>
          <SectionTitle>{item.title}</SectionTitle>

          <SectionContent
            disableGutters={contentDisableGutters}
            disableMinHeight={contentDisableMinHeight}
          >
            {item.content}
          </SectionContent>
        </Section>
      );
    });
  };

  return (
    <Drawer
      anchor="right"
      ModalProps={{
        keepMounted: false,
        disableAutoFocus: true,
        disableEnforceFocus: true,
        disablePortal: disableModalPortal,
        container: DrawerProps?.container,
      }}
      PaperProps={{
        style: {
          overflow: "hidden",
          backgroundColor: "transparent",
          height: "100%",
          width: "100%",
          maxWidth: maxWidth ?? "624px",
          color: "inherit",
        },
      }}
      {...DrawerProps}
    >
      <Container fullHeight={fullHeight}>
        <Main>
          <MainTitle>
            {title && <MainTitleLabel>{title}</MainTitleLabel>}

            <MainTitleCloseButton onClick={(event) => onClose?.(event, "backdropClick")} />
          </MainTitle>

          <MainContent disableGap={disableGap}>
            {children}

            {renderSections()}
          </MainContent>

          {footer && <Footer>{footer}</Footer>}
        </Main>
      </Container>
    </Drawer>
  );
};
